import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppHeader from "@/components/AppHeader";
import Tip from "@/components/Tip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LifeBuoy, Mail, MessageCircleQuestion, Sparkles, FileWarning, Send } from "lucide-react";

const FAQ = [
  { q: "Why isn't my patient receiving WhatsApp messages?", a: "On Twilio sandbox, the patient phone must first send 'join <code>' to +1 415 523 8886. After Meta-verified Business approval this disappears. See WHATSAPP_BUSINESS_SETUP.md in /app/memory." },
  { q: "Why is the Voice-to-EMR transcript wrong?", a: "Whisper auto-detects language. Switch to your spoken language on the Language Switcher before recording. Background noise & multiple speakers reduce accuracy — record from <30cm with one speaker." },
  { q: "Email shows 'simulated'?", a: "Resend is in test mode — verify a domain at resend.com/domains, then update RESEND_FROM in backend/.env. Real Resend Message-IDs will start appearing in /super-admin/email-logs." },
  { q: "Prescription button is blocked?", a: "You must save a signature on your Profile first. Draw it or upload a PNG/JPG (max 2 MB)." },
];

export default function Help() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "",
    clinic_id: user?.clinic_id || "", category: "bug",
    severity: "medium", subject: "", description: "",
  });
  const [sending, setSending] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const submit = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error("Subject and description are required"); return;
    }
    setSending(true);
    try {
      const r = await api.post("/help/report-issue", form);
      setTicketId(r.data.ticket_id);
      toast.success(`Issue logged · ticket #${r.data.ticket_id}`);
      setForm({ ...form, subject: "", description: "" });
    } catch (e) {
      toast.error("Failed to log issue");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen app-bg pb-16">
      <AppHeader title="Help Portal" subtitle="One tap to support · 24/7" badge={user?.role || "guest"} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-6 sm:mt-8 grid md:grid-cols-2 gap-4 sm:gap-6">

        {/* FAQ */}
        <section className="solid-panel p-6 shadow-3d">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 rounded-xl bg-[#e6f0ff] flex items-center justify-center"><MessageCircleQuestion className="h-5 w-5 text-[#004a99]" /></div>
            <div>
              <h2 className="text-xl font-heading font-bold">Common questions <Tip text="Click any item to expand. If your answer isn't here, file a ticket on the right." /></h2>
              <p className="text-sm text-slate-500">Most issues solved in 2 taps.</p>
            </div>
          </div>
          <div className="space-y-2">
            {FAQ.map((f, i) => (
              <details key={i} className="group border border-slate-200 rounded-lg p-3 bg-white hover:shadow-sm transition">
                <summary className="cursor-pointer font-semibold text-slate-800 marker:text-[#f37021] flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#f37021]" /> {f.q}
                </summary>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Report Issue */}
        <section className="solid-panel p-6 shadow-3d">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 rounded-xl bg-[#fff1e8] flex items-center justify-center"><LifeBuoy className="h-5 w-5 text-[#f37021]" /></div>
            <div>
              <h2 className="text-xl font-heading font-bold">Report an issue <Tip text="Sent directly to ram@projexino.com via Resend. You'll get a ticket number for tracking." /></h2>
              <p className="text-sm text-slate-500">Direct line to Projexino engineering.</p>
            </div>
          </div>

          {ticketId && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-center gap-2" data-testid="ticket-confirmation">
              <Mail className="h-4 w-4" /> Ticket <b>#{ticketId}</b> sent. We'll reply to <b>{form.email}</b>.
            </div>
          )}

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Your name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="help-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="help-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="help-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="feature">Feature request</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="help-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical · breaks workflow</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Subject</Label><Input placeholder="e.g. WhatsApp prescriptions not delivered" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="help-subject" /></div>
            <div>
              <Label>Description <Tip text="Include exact steps, patient phone (last 4 digits ok), and timestamps if relevant." /></Label>
              <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="help-description"
                placeholder="What were you trying to do? What did you expect? What actually happened?" />
            </div>
            <Button onClick={submit} disabled={sending} className="w-full bg-[#f37021] hover:bg-[#d9621b] text-white py-6 text-base" data-testid="help-submit">
              {sending ? "Sending…" : <><Send className="h-4 w-4 mr-2" /> Send to ram@projexino.com</>}
            </Button>
            <p className="text-xs text-slate-500 flex items-center gap-1"><FileWarning className="h-3 w-3" /> Tickets are auto-emailed via Resend with Pulse-Agent branding.</p>
          </div>
        </section>
      </main>
    </div>
  );
}
